import { IconPlus } from "@tabler/icons-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Checkbox from "@/components/atomic/Checkbox";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import CategorySelectModal from "@/components/atomic/voc-filter/CategorySelectModal";
import { CategoryGroupItem } from "@/components/ui/CategoryGroupItem";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { cn } from "@/lib/utils";

interface CategoryGroup {
  parentKey: string;
  parentParts: string[];
  childPaths: string[];
}

interface CategoryFilterProps {
  categories: string[];
  onUpdateCategories: (categories: string[]) => void;
}

export const CategoryFilter = (props: Readonly<CategoryFilterProps>) => {
  const { categories, onUpdateCategories } = props;
  const { t } = useTranslation("voiceek_ui");
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const { displayGroups } = useDisplayGroups();

  const handleToggleAllCategories = () => {
    onUpdateCategories([]);
  };

  const onClickWhiteSpace = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-no-bubble]")) {
      e.stopPropagation();
      return;
    }
    setIsCategoryModalOpen(true);
  };

  const groupCategoriesByParent = (categories: string[]): CategoryGroup[] => {
    const grouped = categories.reduce((acc, categoryValue) => {
      const parts = categoryValue.split("/").filter(Boolean);
      const parentKey = parts.length > 1
        ? "/" + parts.slice(0, -1).join("/") + "/"
        : categoryValue;

      if (!acc[parentKey]) {
        acc[parentKey] = [];
      }
      acc[parentKey].push(categoryValue);
      return acc;
    }, {} as Record<string, string[]>);

    return Object.entries(grouped).map(([parentKey, childPaths]) => ({
      parentKey,
      parentParts: parentKey.split("/").filter(Boolean),
      childPaths,
    }));
  };

  const handleCategoryModalSubmit = (_displayGroupId: string, categoryPaths: string[]) => {
    onUpdateCategories(categoryPaths);
    setIsCategoryModalOpen(false);
  };

  return (
    <div className="flex gap-6 items-center">
      <div className="w-[60px] text-[14px] font-semibold text-[#333333]">
        {t("vocFilter.category")}
      </div>
      <div className="w-[2px] bg-[#d9d9d9] self-stretch" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <Checkbox
            id="filter-all-categories"
            checked={categories.length === 0}
            disabled={categories.length === 0}
            onCheckedChange={handleToggleAllCategories}
            className={cn("hover:cursor-pointer")}
          />
          <label htmlFor="filter-all-categories" className="text-[14px] text-[#474747] cursor-pointer">
            {t("vocFilter.all")}
          </label>
        </div>

        <button
          className="bg-white border border-[#b7b7b7] rounded-[5px] p-3 hover:cursor-pointer"
          onClick={(e) => onClickWhiteSpace(e)}
        >
          <div className="flex items-center gap-1.5 text-left text-[#6888E1]">
            <IconPlus className="w-[14px] h-[14px]" />
            <span className="text-[14px]">
              {t("vocFilter.selectCategory")}
            </span>
          </div>

          {categories.length > 0 && (
            <ScrollArea
              className="mt-2"
              viewportClassName="max-h-22"
            >
              <div className="flex flex-col gap-2 pr-3">
                {groupCategoriesByParent(categories).map(({ parentKey, parentParts, childPaths }) => (
                  <CategoryGroupItem
                    key={parentKey}
                    parentParts={parentParts}
                    childPaths={childPaths}
                    onRemove={() => {
                      const remainingCategories = categories.filter(c => !childPaths.includes(c));
                      onUpdateCategories(remainingCategories);
                    }}
                  />
                ))}
              </div>
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          )}
        </button>
      </div>

      {/* CategorySelectModal */}
      {displayGroups && (
        <CategorySelectModal
          displayGroups={displayGroups}
          initialSelectedPaths={categories || []}
          isOpenModal={isCategoryModalOpen}
          setIsOpenModal={setIsCategoryModalOpen}
          submit={handleCategoryModalSubmit}
        />
      )}
    </div>
  );
};
